// frontend/src/pages/Pricing/SubscriptionSuccess.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, Button, CircularProgress } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import api from '../../services/api';

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [tier, setTier] = useState('pro');

  useEffect(() => {
    const verifySubscription = async () => {
      try {
        // Give webhook a moment to process
        await new Promise(r => setTimeout(r, 2000));

        const res = await api.get('/api/subscription/status');
        if (res.data.success && res.data.data.tier !== 'free') {
          setTier(res.data.data.tier);
          setStatus('success');
        } else {
          // Webhook may not have arrived yet — retry once
          await new Promise(r => setTimeout(r, 3000));
          const retry = await api.get('/api/subscription/status');
          if (retry.data.success && retry.data.data.tier !== 'free') {
            setTier(retry.data.data.tier);
            setStatus('success');
          } else {
            setStatus('pending');
          }
        }
      } catch (err) {
        setStatus('error');
      }
    };
    verifySubscription();
  }, []);

  return (
    <Container maxWidth="sm">
      <Box sx={{ textAlign: 'center', py: 8 }}>
        {status === 'loading' && (
          <>
            <CircularProgress size={48} sx={{ mb: 3 }} />
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Confirming your subscription...
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please wait while we verify your payment.
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleOutlineIcon sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Welcome to MathZA {tier.charAt(0).toUpperCase() + tier.slice(1)}!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Your subscription is active. All premium features are now unlocked.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/home')}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 4 }}
            >
              Start Studying
            </Button>
          </>
        )}

        {status === 'pending' && (
          <>
            <CheckCircleOutlineIcon sx={{ fontSize: 72, color: 'info.main', mb: 2 }} />
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Payment received!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Your subscription is being activated. This usually takes less than a minute.
              If your features aren't unlocked yet, refresh the page or check back shortly.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/home')}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Go to Home
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              We couldn't verify your payment. If you were charged, please contact us and we'll sort it out.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button variant="outlined" onClick={() => navigate('/home')} sx={{ borderRadius: 2, textTransform: 'none' }}>
                Go to Home
              </Button>
              <Button
                variant="contained"
                onClick={() => window.location.href = 'mailto:hello@pedaplate.co.za?subject=Subscription Issue'}
                sx={{ borderRadius: 2, textTransform: 'none' }}
              >
                Contact Support
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Container>
  );
};

export default SubscriptionSuccess;
